import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPurchase, completePurchase, getUserData } from '../lib/storage';
import { formatPrice } from '../data/packages';
import '../styles/confirmar.css';

const ConfirmarCompra = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const selectedPackage = location.state?.package;
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'pending' | 'success' | 'failed'
  const [purchasedData, setPurchasedData] = useState(null);

  if (!selectedPackage) {
    navigate('/tienda');
    return null;
  }

  const handlePayment = async (method) => {
    setPaymentMethod(method);
    setIsProcessing(true);
    setPaymentStatus('pending');

    try {
      // Create purchase record
      const purchaseId = createPurchase(selectedPackage, method);

      // Simulate payment processing (MOCK)
      // In production, this would integrate with Mercado Pago or Wompi
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Complete purchase (add tickets to user)
      const result = completePurchase(purchaseId);

      if (result.success) {
        setPaymentStatus('success');
        setPurchasedData(result.purchase);
      } else {
        setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus('failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoToRuleta = () => {
    navigate('/');
  };

  // Success Screen
  if (paymentStatus === 'success') {
    return (
      <div className="confirmar-container success-screen">
        <div className="success-content">
          <div className="success-icon">✅</div>
          <h1>¡Pago exitoso!</h1>
          <div className="success-details">
            <p className="success-package">{selectedPackage.name}</p>
            <div className="success-tickets">
              {selectedPackage.normalTickets > 0 && (
                <p>🎫 +{selectedPackage.normalTickets} tiquetes normales</p>
              )}
              {selectedPackage.goldenTickets > 0 && (
                <p>⭐ +{selectedPackage.goldenTickets} tiquetes dorados</p>
              )}
            </div>
            <p className="success-msg">Tus tiquetes han sido agregados</p>
          </div>
          <button className="btn-go-ruleta" onClick={handleGoToRuleta}>
            🎰 Ir a la Ruleta
          </button>
        </div>
      </div>
    );
  }

  // Failed Screen
  if (paymentStatus === 'failed') {
    return (
      <div className="confirmar-container failed-screen">
        <div className="failed-content">
          <div className="failed-icon">❌</div>
          <h1>Pago fallido</h1>
          <p>No pudimos procesar tu pago. Por favor intenta de nuevo.</p>
          <div className="failed-buttons">
            <button className="btn-retry" onClick={() => setPaymentStatus(null)}>
              Intentar de nuevo
            </button>
            <button className="btn-cancel" onClick={() => navigate('/tienda')}>
              Volver a la tienda
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Processing Screen
  if (isProcessing) {
    return (
      <div className="confirmar-container processing-screen">
        <div className="processing-content">
          <div className="processing-spinner"></div>
          <h2>Procesando pago...</h2>
          <p>Por favor espera mientras confirmamos tu pago con {paymentMethod}</p>
        </div>
      </div>
    );
  }

  // Main Confirmation Screen
  return (
    <div className="confirmar-container">
      {/* Back Button */}
      <button className="back-button" onClick={() => navigate('/tienda')}>
        ←
      </button>

      {/* Header */}
      <div className="confirmar-header">
        <h1>Confirmar Compra</h1>
      </div>

      {/* Package Summary */}
      <div className="package-summary">
        <div className="summary-icon">
          {selectedPackage.type === 'golden' ? '⭐' : selectedPackage.type === 'mixed' ? '🎁' : '🎫'}
        </div>
        <h2 className="summary-name">{selectedPackage.name}</h2>
        <p className="summary-desc">{selectedPackage.description}</p>
        
        <div className="summary-details">
          <h3>Incluye:</h3>
          {selectedPackage.normalTickets > 0 && (
            <div className="detail-item">
              <span className="detail-icon">🎫</span>
              <span>{selectedPackage.normalTickets} tiquetes normales</span>
              <span className="detail-expiry">Expiran en 24h</span>
            </div>
          )}
          {selectedPackage.goldenTickets > 0 && (
            <div className="detail-item golden">
              <span className="detail-icon">⭐</span>
              <span>{selectedPackage.goldenTickets} tiquetes dorados</span>
              <span className="detail-expiry">No expiran</span>
            </div>
          )}
        </div>

        <div className="summary-price">
          <span>Total a pagar:</span>
          <span className="price">{selectedPackage.priceFormatted}</span>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="payment-methods">
        <h3>Selecciona método de pago</h3>
        
        <button 
          className="payment-btn mercadopago"
          onClick={() => handlePayment('Mercado Pago')}
        >
          <div className="payment-logo">💳</div>
          <div className="payment-info">
            <span className="payment-name">Mercado Pago</span>
            <span className="payment-desc">Tarjeta, PSE, efectivo</span>
          </div>
          <span className="payment-arrow">→</span>
        </button>

        <button 
          className="payment-btn wompi"
          onClick={() => handlePayment('Wompi')}
        >
          <div className="payment-logo">💳</div>
          <div className="payment-info">
            <span className="payment-name">Wompi</span>
            <span className="payment-desc">Pago seguro en línea</span>
          </div>
          <span className="payment-arrow">→</span>
        </button>
      </div>

      {/* Disclaimer */}
      <div className="disclaimer">
        <p>🔒 Pago seguro. Compra de tiquetes de participación.</p>
        <p className="small">Al continuar, aceptas los términos y condiciones.</p>
      </div>

      {/* Cancel Button */}
      <button className="btn-cancel-purchase" onClick={() => navigate('/tienda')}>
        Cancelar
      </button>
    </div>
  );
};

export default ConfirmarCompra;